import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type MetaMap = Record<string, string | undefined>;

function ensureMeta(name: string, value?: string, attr: 'name' | 'property' = 'name') {
  if (!value && value !== '') return;
  const selector = attr === 'name' ? `meta[name="${name}"]` : `meta[property="${name}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    if (attr === 'name') el.setAttribute('name', name);
    else el.setAttribute('property', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value || '');
}

function ensureLink(rel: string, href?: string) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function absoluteUrl(path?: string) {
  if (!path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return path.startsWith('/') ? origin + path : `${origin}/${path}`;
}

function removeExistingJsonLd() {
  document.head.querySelectorAll('script[data-seo-jsonld]').forEach((n) => n.remove());
}

function injectJsonLd(obj: object | object[]) {
  removeExistingJsonLd();
  const script = document.createElement('script');
  script.setAttribute('type', 'application/ld+json');
  script.setAttribute('data-seo-jsonld', 'true');
  script.textContent = JSON.stringify(obj);
  document.head.appendChild(script);
}

function applyMeta(meta: MetaMap) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  if (meta.title) document.title = meta.title;
  ensureMeta('description', meta.description);
  ensureMeta('keywords', meta.keywords);
  ensureMeta('robots', meta.robots || 'index, follow');

  ensureMeta('og:title', meta.ogTitle || meta.title, 'property');
  ensureMeta('og:description', meta.ogDescription || meta.description, 'property');
  ensureMeta('og:type', meta.ogType || 'website', 'property');

  // make sure URLs are absolute
  const ogUrl = meta.ogUrl || meta.canonical || (origin ? origin + '/' : '/');
  ensureMeta('og:url', ogUrl, 'property');
  ensureMeta('og:locale', meta.ogLocale || 'en_KE', 'property');

  // normalize social images to absolute URLs and set alt text meta
  const rawImage = meta.ogImage || meta.twitterImage || '/images/hero.jpeg';
  const absImage = absoluteUrl(rawImage);
  ensureMeta('og:image', absImage, 'property');
  ensureMeta('og:image:alt', meta.ogImageAlt || meta.description?.slice(0, 120));

  ensureMeta('twitter:card', meta.twitterCard || 'summary_large_image');
  ensureMeta('twitter:title', meta.twitterTitle || meta.title);
  ensureMeta('twitter:description', meta.twitterDescription || meta.description);
  ensureMeta('twitter:image', absImage);
  ensureMeta('twitter:image:alt', meta.twitterImageAlt || meta.description?.slice(0, 120));

  // canonical
  if (meta.canonical) ensureLink('canonical', meta.canonical);
  // sitemap link (static)
  ensureLink('sitemap', '/sitemap.xml');
}

function createStructuredData(meta: MetaMap, pathname: string) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const siteUrl = meta.canonical || meta.ogUrl || (origin ? origin + pathname : pathname);
  const image = absoluteUrl(meta.ogImage || '/images/hero.jpeg') || undefined;

  const org = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'WAAFA Construction Limited',
    url: origin || 'https://waafaconstruction.com',
    logo: absoluteUrl('/images/hero.svg') || undefined,
    sameAs: ['https://facebook.com/waafaconstruction', 'https://instagram.com/waafaconstruction']
  } as Record<string, any>;

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: origin || 'https://waafaconstruction.com',
    name: meta.title,
    potentialAction: {
      '@type': 'SearchAction',
      'target': `${origin}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  const items: object[] = [org, website];

  // Breadcrumbs
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length > 0) {
    const itemListElement = [{ '@type': 'ListItem', position: 1, name: 'Home', item: origin || '/' }]
      .concat(
        parts.map((p, i) => ({
          '@type': 'ListItem',
          position: i + 2,
          name: decodeURIComponent(p).replace(/[-_]/g, ' '),
          item: `${origin}/${parts.slice(0, i + 1).join('/')}`
        }))
      );

    items.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement
    });
  }

  // Page-level schema: Article / Product
  if (/^\/project\//i.test(pathname)) {
    items.push({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: meta.title,
      image: image ? [image] : undefined,
      description: meta.description,
      url: siteUrl,
      brand: { '@type': 'Organization', name: 'WAAFA Construction Limited' }
    });
  } else if (/^\/(news|blogs)\b/i.test(pathname)) {
    items.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: meta.title,
      description: meta.description,
      image: image ? [image] : undefined,
      url: siteUrl,
      author: { '@type': 'Organization', name: 'WAAFA Construction Limited' },
      publisher: { '@type': 'Organization', name: 'WAAFA Construction Limited' }
    });
  }

  return items.length === 1 ? items[0] : items;
}

const routesMeta: { match: RegExp; meta: MetaMap }[] = [
  {
    match: /^\/$/,
    meta: {
      title: 'WAAFA Construction Limited | Premium Property Developers Mombasa Kenya',
      description:
        'WAAFA Construction Limited - Leading property developers in Mombasa, Kenya. Luxury homes, commercial properties, and diaspora investment solutions.',
      keywords: 'property developers Mombasa Kenya, luxury homes Tudor, real estate investment Kenya, Waafa Construction',
      ogImage: '/images/hero.jpeg',
      ogUrl: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
      canonical: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
    },
  },
  {
    match: /^\/about(us)?$/i,
    meta: {
      title: 'About Us - WAAFA Construction Limited',
      description: 'Learn about WAAFA Construction Limited — our mission, values, and track record in Mombasa real estate development.',
    },
  },
  {
    match: /^\/projects(\/|$)/i,
    meta: {
      title: 'Projects - WAAFA Construction Limited',
      description: 'Explore our ongoing and completed real estate projects in Mombasa including residential and commercial developments.',
    },
  },
  {
    match: /^\/contact$/i,
    meta: {
      title: 'Contact - WAAFA Construction Limited',
      description: 'Get in touch with WAAFA Construction Limited for enquiries on property development, sales, and diaspora investment.',
    },
  },
  {
    match: /^\/blogs|^\/news|^\/diaspora-forum/i,
    meta: {
      title: 'News & Blogs - WAAFA Construction',
      description: 'Latest news, blogs and updates from WAAFA Construction Limited. Stay informed about new projects and investment opportunities.',
    },
  },
  {
    match: /^\/project\/.*$/i,
    meta: {
      title: 'Project - WAAFA Construction',
      description: 'Project details and specifications. Learn more about our developments and floor plans.',
      ogType: 'article',
    },
  },
];

export default function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const matched = routesMeta.find((r) => r.match.test(pathname));
    const defaultMeta: MetaMap = {
      title: 'WAAFA Construction Limited | Property Developers Mombasa, Kenya',
      description:
        'WAAFA Construction Limited — premium real estate developers in Mombasa, Kenya. Residential and commercial developments, investment plans for the diaspora.',
      keywords: 'waafa construction, property developers mombasa, real estate kenya',
      ogImage: '/images/hero.jpeg',
      ogUrl: typeof window !== 'undefined' ? window.location.origin + pathname : pathname,
      canonical: typeof window !== 'undefined' ? window.location.origin + pathname : pathname,
    };

    const meta = { ...defaultMeta, ...(matched ? matched.meta : {}) };

    // If current path is project with slug, try to include slug in title
    if (/^\/project\//i.test(pathname)) {
      const slug = pathname.split('/').pop();
      if (slug) meta.title = `${decodeURIComponent(slug).replace(/[-_]/g, ' ')} | ${meta.title}`;
    }

    applyMeta(meta);

    // inject structured data for Google
    const jsonLd = createStructuredData(meta, pathname);
    if (jsonLd) injectJsonLd(jsonLd);

    // mark pages that should be indexed (default) — keep index, follow
    ensureMeta('robots', 'index, follow');

    // add theme-color for mobile browsers
    ensureMeta('theme-color', '#14B8A6');

    // cleanup when unmounting / path change
    return () => {
      removeExistingJsonLd();
    };
  }, [pathname]);

  return null;
}
